import { memo, useCallback, useState } from 'react';
import NullComponent from '../../baseComponents/NullComponent';
import { memoChecker } from '../../utils/globals/components';
import { returnClassOfGridTemplate } from '../../utils/globals/tailwind';
import { getValueForElement } from '../../utils/globals/valueOrDefaultValue';
import { addAntiUnsaveElement, deleteAntiUnsaveElement } from '../../utils/reducers/reduxDispatch';
import Checkbox, { CheckboxMargin } from '../Checkbox';
import CustomSelect, { CustomSelectBase, CustomSelectSearchBarWithNotInFilteredItems, CustomSelectWithNotInFilteredItems, CustomSelectWithSearchBar } from '../CustomSelect';
import ImageInput, { ImageInputStateLess } from '../ImageInput';
import MultipleCheckboxSelector, { MultipleCheckboxSelectorStateLess } from '../MultipleCheckboxSelector';
import { InputDefault, InputHidden, InputNumberWithLabelMargin, InputNumberWithLabelMarginStateFull, InputNumberWithLabelMarginTextRight, InputNumberWithLabelMarginTextRightDebounce, InputNumberWithLabelMarginTextRightStateFull, InputWithLabelMargin, InputWithLabelMarginHalfFloatStateFull, InputWithLabelMarginHalfFloatStateLess, InputWithLabelMarginIntegerStateFull, InputWithLabelMarginIntegerStateLess, InputWithLabelMarginRemove, InputWithLeftIcon } from '../Input';
import TwinEditor from '../TwinEditor';
import { AllFormRenderComponents, AllSectionsClassNameType, FormElement, FormRenderDisplayChecker, FormRendererProps, FormSection } from './types';
import { ChangeValueFunc } from '../../utils/globals/types';
import CustomSelectSections, { CustomSelectSectionsSearchbar } from '../CustomSelect/CustomSelectSections';
import CustomSelectBlockedItems, { CustomSelectBlockedItemsSearchbar } from '../CustomSelect/CustomSelectBlockedItems';
import { InputCalendarStateFull } from '../Input/InputCalendar';
import { ListAdderStateFull, ListAdderStateLess } from '../ListAdder';
import TextArea, { TextAreaStateFull } from '../Input/TextArea';
import InputColor, { InputColorDeleteButtonWithMargin, InputColorWithMargin } from '../Input/InputColor';
import InputPassword, { InputPasswordWithLabel } from '../Input/InputPassword';
import InputHour from '../Input/InputHour';
import InputHourUnlimited from '../Input/InputHour/InputHourUnlimited';
import { TextField } from '../TextField';
import InputIcon, { InputIconWithMargin } from '../Input/InputIcon';
import FilesInput from '../FilesInput';
import { InputCalendarWithHoursStateFull, InputCalendarWithHoursStateLess } from '../Input/InputCalendarWithHours';
import { InputCalendarWithStartEndHoursStateLess } from '../../specificComponents/Task/InputCalendarWithStartEndHours'
import { InputHourMinSecUnlimitedStateFull, InputHourMinSecUnlimitedStateLess } from '../Input/InputHourMinSec/InputHourMinSecUnlimited';
import { InputHourMinSecStateFull, InputHourMinSecStateLess } from '../Input/InputHourMinSec';
import InputCurrency from '../Input/InputCurrency';
import InputCurrencyStateFull from '../Input/InputCurrency/InputCurrencyStateFull';
import InputPrice, { InputPriceStateFull } from '../Input/InputPrice';
import { DisplayPriceField } from '../DisplayPriceField';
import './formRenderer.sass'
import InputDiscount from '../Input/InputDiscount';
import { TwinDictionary } from '../../utils/globals/dictionary';
import { CustomSelectSearchBarWithCheckBoxStateFull, CustomSSearchBarWithHeaderInnerTags } from '../CustomSelect/CustomSelectTags';
import CustomSelectColor from '../CustomSelect/CustomSelectColor';

const FormRenderer: React.FC<FormRendererProps> = ({ className, children, ...props }) => {
    const {sectionsRendered} = useSectionsRendererLogic(props)
    return (
        <div className={'grid grid-cols-1 gap-y-38 form-renderer flex-auto ' + (className || '')}>
            {sectionsRendered}
            {children}
        </div>
    );
}

const useSectionsRendererLogic = (props: FormRendererProps) => {
    const { sections, allSectionsClassName, allSectionsInnerClassName, allExtraDataComponentProps } = props
    const sectionsRendered: JSX.Element[] = []

    for (const sectionKey in sections) {
        const section = sections[sectionKey]
        const sectionChilds = renderSectionChilds(section.elements as any, props.antiUnsaveTrigger, props.displayChecker, allExtraDataComponentProps)
        if (sectionChilds.length) {
            sectionsRendered.push(
                <FormRendererSection key={sectionKey} section={section} allSectionsClassName={allSectionsClassName} allSectionsInnerClassName={allSectionsInnerClassName} >
                    {sectionChilds}
                </FormRendererSection>
            )
        }
    }
        
    return {sectionsRendered}
}
type FormRendererSectionProps = AllSectionsClassNameType & {
    section: FormSection
    children?: React.ReactNode
};

const FormRendererSection: React.FC<FormRendererSectionProps> = ({section, allSectionsClassName, allSectionsInnerClassName, children}) => {
    const gridCols = returnClassOfGridTemplate(section.cols)
    return (
        <div className={allSectionsClassName || ''}>
            {section.title && <div className='regular18 mb-17'>{section.title}</div>}
            <div className={'gap-x-40 gap-y-15 grid ' + gridCols + ' ' + (section.className || '') + ' ' + (allSectionsInnerClassName || '') }>{children}</div>
        </div>
    );
}

const renderSectionChilds = (sectionElements: FormElement[], antiUnsaveTrigger?: boolean, displayChecker?: FormRenderDisplayChecker, allExtraDataComponentProps?: TwinDictionary) => {
    const sectionElementsRenderd: JSX.Element[] = []
    for (const sectionElement of sectionElements) {
        if (displayChecker && !displayChecker(sectionElement)) {
            continue
        }
        sectionElementsRenderd.push(<SingleElementsRenderer component={sectionElement.component} params={sectionElement} allExtraDataComponentProps={allExtraDataComponentProps} antiUnsaveTrigger={antiUnsaveTrigger} key={sectionElement.name} />)
    }
    return sectionElementsRenderd
}

export type FormRendererSingleElements = Record<AllFormRenderComponents, React.FC<any>>

const elements : FormRendererSingleElements = {
    Checkbox: Checkbox,
    CheckboxMargin: CheckboxMargin,
    Custom: NullComponent,
    CustomSelect: CustomSelect,
    CustomSelectBase: CustomSelectBase,
    CustomSelectBlockedItems: CustomSelectBlockedItems,
    CustomSelectBlockedItemsSearchbar: CustomSelectBlockedItemsSearchbar,
    CustomSelectColor: CustomSelectColor,
    CustomSelectSections: CustomSelectSections,
    CustomSelectSectionsSearchbar: CustomSelectSectionsSearchbar,
    CustomSelectWithNotInFilteredItems: CustomSelectWithNotInFilteredItems,
    CustomSelectSearchBarWithNotInFilteredItems: CustomSelectSearchBarWithNotInFilteredItems,
    CustomSelectSearchBarWithCheckBoxStateFull,
    CustomSSearchBarWithHeaderChildrenTags: CustomSSearchBarWithHeaderInnerTags,
    CustomSelectWithSearchBar: CustomSelectWithSearchBar,
    DisplayPriceField: DisplayPriceField,
    FilesInput: FilesInput,
    ImageInput: ImageInput,
    ImageInputStateLess: ImageInputStateLess,
    Input: InputDefault,
    InputCalendarStateFull: InputCalendarStateFull,
    InputCalendarWithHoursStateLess: InputCalendarWithHoursStateLess,
    InputCalendarWithHoursStateFull: InputCalendarWithHoursStateFull,
    InputCalendarWithStartEndHoursStateLess: InputCalendarWithStartEndHoursStateLess,
    InputColor: InputColor,
    InputColorWithMargin: InputColorWithMargin,
    InputColorDeleteButtonWithMargin: InputColorDeleteButtonWithMargin,
    InputCurrency: InputCurrency,
    InputCurrencyStateFull: InputCurrencyStateFull,
    InputHidden: InputHidden,
    InputHour: InputHour,
    InputHourUnlimited: InputHourUnlimited,
    InputHourMinSecStateFull: InputHourMinSecStateFull,
    InputHourMinSecStateLess: InputHourMinSecStateLess,
    InputHourMinSecUnlimitedStateFull: InputHourMinSecUnlimitedStateFull,
    InputHourMinSecUnlimitedStateLess: InputHourMinSecUnlimitedStateLess,
    InputIcon: InputIcon,
    InputIconWithMargin: InputIconWithMargin,
    InputNumberWithLabelMargin: InputNumberWithLabelMargin,
    InputNumberWithLabelMarginStateFull: InputNumberWithLabelMarginStateFull,
    InputNumberWithLabelMarginTextRight: InputNumberWithLabelMarginTextRight,
    InputNumberWithLabelMarginTextRightStateFull: InputNumberWithLabelMarginTextRightStateFull,
    InputPassword: InputPassword,
    InputPasswordWithLabel: InputPasswordWithLabel,
    InputDiscount: InputDiscount,
    InputPrice: InputPrice,
    InputPriceStateFull: InputPriceStateFull,
    InputWithLabelMargin: InputWithLabelMargin,
    InputWithLabelMarginIntegerStateFull: InputWithLabelMarginIntegerStateFull,
    InputWithLabelMarginIntegerStateLess: InputWithLabelMarginIntegerStateLess,
    InputWithLabelMarginHalfFloatStateFull: InputWithLabelMarginHalfFloatStateFull,
    InputWithLabelMarginHalfFloatStateLess: InputWithLabelMarginHalfFloatStateLess,
    InputWithLabelMarginRemove: InputWithLabelMarginRemove,
    InputNumberWithLabelMarginTextRightDebounce,
    InputWithLeftIcon: InputWithLeftIcon,
    ListAdderStateFull: ListAdderStateFull,
    ListAdderStateLess: ListAdderStateLess,
    MultipleCheckboxSelector: MultipleCheckboxSelector,
    MultipleCheckboxSelectorStateLess: MultipleCheckboxSelectorStateLess,
    TextArea: TextArea,
    TextAreaStateFull: TextAreaStateFull,
    TextField: TextField,
    TwinEditor: TwinEditor,
}

interface SingleElementsRendererProps extends SingleElementsRendererLogicProps  {
    component: keyof FormRendererSingleElements
    allExtraDataComponentProps?: TwinDictionary
}

export const SingleElementsRenderer: React.FC<SingleElementsRendererProps> = memo(({ component, params, antiUnsaveTrigger, allExtraDataComponentProps }) => {
    const { onChange, onFinalChange } = useSingleElementsRendererLogic({ params, antiUnsaveTrigger })
    const MyComponent = params.component === 'Custom' ? params.render : elements[component]
    return <MyComponent key={params.name} {...params} {...allExtraDataComponentProps} onChange={onChange} onFinalChange={onFinalChange} />
}, (oldProps, nextProps) => memoChecker(oldProps, nextProps, ['component', 'params', 'antiUnsaveTrigger']))

interface SingleElementsRendererLogicProps {
    params: FormElement
    antiUnsaveTrigger?: boolean
}

const useSingleElementsRendererLogic = ({ params, antiUnsaveTrigger }: SingleElementsRendererLogicProps) => {
    const [initialValue] = useState(String(getValueForElement(params.component, params as any)) || '')
    const name = params.name
    const paramsOnChange = params.onChange
    const paramsOnFinalChange = (params as any).onFinalChange
    const onChange: ChangeValueFunc = useCallback(async (value) => {
        paramsOnChange?.(value)
        if(String(value) === '' && initialValue === 'undefined'){
            await deleteAntiUnsaveElement(name)
        }else if (String(value) !== initialValue) {
            await addAntiUnsaveElement([name, value])
        } else {
            await deleteAntiUnsaveElement(name)
        }
    }, [initialValue, name, paramsOnChange])
    const onFinalChange: ChangeValueFunc = useCallback(async (value) => {
        paramsOnFinalChange?.(value)
        if(String(value) === '' && initialValue === 'undefined'){
            await deleteAntiUnsaveElement(name)
        }else if (String(value) !== initialValue) {
            await addAntiUnsaveElement([name, value])
        } else {
            await deleteAntiUnsaveElement(name)
        }
    }, [initialValue, name, paramsOnFinalChange])
    return { onChange: antiUnsaveTrigger ? onChange : params.onChange, onFinalChange: antiUnsaveTrigger ? onFinalChange : paramsOnFinalChange }
}


export default FormRenderer;